// Network: everything the frame controller sends to the server, the shot ids, resync, and how an incoming
// game:state is routed (apply now, hold it until the replay catches up, or ignore it because our shot is still on).
//
// Sync approach (online): the shooter's browser is authoritative for its own shot. It runs the physics locally, records the
// first ball hit and the pots (shot.ts), and streams sampled ball positions (~20/s) through the server to the
// opponent, who replays them with a small buffer instead of simulating (playback.ts). When all balls stop, the
// shooter sends the result; the server validates it, applies the snooker rules (shared/rules.ts) and broadcasts the
// new state. Both browsers snap to that state (respotted colours, cue ball in hand), so the tables never drift apart.
//
// The other incoming messages are handled by their concern: shot-start / frames in playback.ts, cue-move in
// ballInHand.ts, notice in endOfGame.ts, match-found in hud.ts. socket-state.ts calls them all through Match.
import {SocketState} from "../features/socketio/socket-state";
import {GameStore} from "../features/game/game-store";
import type {ShotReport, Vec} from "../shared/rules";
import type {MatchCore, OnlineInfo, Phase, StreamFrame} from "./types";
import type {
    CueMoveOut, FramesOut, GameRef, GameStateMsg, ShotResultAck, ShotResultOut, ShotStartOut, SyncOut,
} from "./messages";

/** A fresh id for one of our shots (the server echoes it back while the shot is pending). */
export function newShotId(now = Date.now(), random = Math.random()): string {
    return random.toString(36).slice(2, 10) + now.toString(36);
}

/** Client → server messages, typed. */
export const Net = {
    shotStart(msg: ShotStartOut) { SocketState.emit("game:shot-start", msg); },
    frames(msg: FramesOut) { SocketState.emit("game:frames", msg); },
    shotResult(msg: ShotResultOut, ack: (r: ShotResultAck) => void) { SocketState.emit("game:shot-result", msg, ack); },
    cueMove(msg: CueMoveOut) { SocketState.emit("game:cue-move", msg); },
    leave(msg: GameRef) { SocketState.emit("game:leave", msg); },
    concede(msg: GameRef) { SocketState.emit("game:concede", msg); },
    rematch(msg: GameRef) { SocketState.emit("game:rematch", msg); },
    /** The socket (re)connected, or a replay went quiet: ask for the current game state. */
    sync(msg: SyncOut) { SocketState.emit("game:sync", msg); },
};

export function shotResultMessage(online: OnlineInfo, shotId: string, report: ShotReport): ShotResultOut {
    return {gameId: online.gameId, seq: online.seq, shot: shotId, ...report};
}

export function framesMessage(online: OnlineInfo, shotId: string, frames: StreamFrame[]): FramesOut {
    return {gameId: online.gameId, seq: online.seq, shot: shotId, frames};
}

export function shotStartMessage(online: OnlineInfo, shotId: string, cueStart: Vec): ShotStartOut {
    return {gameId: online.gameId, seq: online.seq, shot: shotId, cueStart};
}

// ----- routing an incoming game:state -----

export type StateAction =
    | "apply"               // snap to it now (Match.applyServerState)
    | "defer"               // the opponent's shot is still replaying: apply it when the replay catches up
    | "keep-shot"           // our own shot is still on (rolling, or waiting for the result): ignore it
    | "adopt-pending-shot"; // the replay we are showing is the shot the server still has pending: keep replaying

export interface StateRouting {
    phase: Phase;
    online: OnlineInfo | null;
    activeShotId: string | null;
    playback: {shotId: string | null} | null;
}

/** What to do with a game:state (after the left-games check and its toasts). Pure. */
export function routeState(m: StateRouting, msg: GameStateMsg): {action: StateAction, pendingShot: string | null} {
    const sameGame = !!m.online && msg.gameId === m.online.gameId;
    const sameSeq = sameGame && msg.seq === m.online!.seq;
    const pendingShot: string | null = msg.shot && msg.status === "playing" ? msg.shot.id : null;
    // The server still has a shot pending and this state is from before it: keep the shot going.
    if (sameSeq && !msg.error && pendingShot) {
        if (m.phase === "spectating" && m.playback && (!m.playback.shotId || m.playback.shotId === pendingShot)) {
            return {action: "adopt-pending-shot", pendingShot};
        }
        if ((m.phase === "shooting" || m.phase === "waiting") && pendingShot === m.activeShotId) return {action: "keep-shot", pendingShot};
    }
    // The shot was abandoned: don't play the rest of it back, go straight to the restored table.
    if (msg.abandoned) return {action: "apply", pendingShot};
    // While a shot is playing back, apply the result once the playback catches up.
    if (m.phase === "spectating" && m.playback && m.online && msg.gameId === m.online.gameId && msg.seq > m.online.seq) {
        return {action: "defer", pendingShot};
    }
    // Our own shot is still rolling and the server hasn't seen it start yet (its shot-start may still be
    // buffered after a reconnect): wait for the result.
    if (m.phase === "shooting" && sameSeq && !msg.error && !pendingShot) return {action: "keep-shot", pendingShot};
    return {action: "apply", pendingShot};
}

/** socket "game:state" */
export function onState(m: MatchCore, msg: GameStateMsg | null | undefined) {
    if (!msg || !msg.frame) return;
    if (msg.receivedAt === undefined) msg.receivedAt = Date.now();
    // We already closed this finished frame: its state must not bring the end-of-frame dialog back.
    if (m.leftGameIds.has(msg.gameId)) return;
    if (msg.notice) GameStore.toast(msg.notice, "warn", 6000);
    if (msg.error) GameStore.toast(msg.error, "warn", 6000);
    const {action, pendingShot} = routeState(m, msg);
    switch (action) {
        case "adopt-pending-shot":
            m.playback!.shotId = pendingShot;
            m.playback!.lastData = performance.now();
            return;
        case "keep-shot":
            return;
        case "defer":
            m.playback!.pendingState = msg;
            m.playback!.stateAt = performance.now();
            return;
        case "apply":
            m.applyServerState(msg);
    }
}

/** Our view of the online game after a game:state. */
export function onlineInfoFrom(msg: GameStateMsg, now = Date.now()): OnlineInfo {
    const c = msg.clock;
    const clock = c && msg.status === "playing" && typeof c.remainingMs === "number"
        ? {turn: c.turn, deadline: (msg.receivedAt ?? now) + c.remainingMs, totalMs: c.totalMs}
        : null;
    return {
        gameId: msg.gameId, seq: msg.seq, you: msg.you, players: msg.players, status: msg.status, rematch: msg.rematch || [false, false],
        ranked: !!msg.ranked, clock, elo: Array.isArray(msg.elo) && msg.elo.length === 2 ? msg.elo : null,
    };
}

/** Our shot is resolved (applied, rejected or abandoned) unless the server says it is still pending. */
export function shotStillPending(msg: GameStateMsg, activeShotId: string | null, prevSeq: number): boolean {
    return !!(msg.shot && msg.shot.id === activeShotId && msg.seq === prevSeq);
}
